import { skipToken } from '@apollo/client/react'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
} from '@mui/material'
import { compact, noop } from 'lodash-es'
import {
  type ReactNode,
  type RefObject,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import { match } from 'ts-pattern'
import FluentChevronLeft20Regular from '~icons/fluent/chevron-left-20-regular'
import IcBaselineStar from '~icons/ic/baseline-star'
import PersonalArtworkList from 'src/components/PersonalArtworkList'
import UserLikedArtworkList from 'src/components/PersonalArtworkList/UserLikedArtworkList'
import Text from 'src/components/ui/Text'
import UserAssetsTab from 'src/components/UserProfile/UserAssetsTab'
import UserDecorationsTab from 'src/components/UserProfile/UserDecorationsTab'
import UserCreditView from 'src/components/UserProfile/views/UserCreditView'
import { useInvalidateCouponList } from 'src/features/Coupon/Regular/states'
import { useMyInfo } from 'src/services/apollo/auth'
import { useGetBookmark } from 'src/services/apollo/bookmark'
import type { Bookmark } from 'src/services/apollo/generated/graphql'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { useDynamicConstants } from 'src/services/constants'
import {
  addCollectionItem,
  isDefaultCollection,
  useCollections,
} from 'src/services/helpers/bookmarkCollection'
import { OrderOption } from 'src/services/helpers/orderOptionParams'
import { useQs } from 'src/utils/qs'
import { PLATFORM_ROLES } from 'src/utils/rbac/constants'
import { useHash } from 'src/utils/router/useHash'
import { getUserMetaDescription, getUserTitle } from 'src/utils/seo'
import { track, useTrackPageView } from 'src/utils/tracker'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { shouldNotHappen } from '../../utils/error'
import { UserLikedModelItemList } from '../GenerationModel/GenerationModelList/ProfileModelItemList'
import UserModelList from '../GenerationModel/UserModelList'
import { NavToTrainLoraPageButton } from '../GrowthUnit/TrainLoraEvent'
import MetaTags from '../MetaTags'
import { Preloader } from '../Preloader/Preloader'
import { UILink } from '../ui/Link'
import { ProfilePageTab } from './constants'
import UserProfileSubTab from './Content/UserProfileSubTab'
import { useEditItems } from './EditItems'
import ProfileBookmarkItemList from './ProfileBookmarkList'
import ProfileFilesTab from './ProfileFilesTab'
import {
  navigateWithScrollLock,
  saveProfileScroll,
} from './profileScrollRestore'
import ProfileTaskItemList from './ProfileTaskList'
import ProfileTaskTab from './ProfileTaskTab'
import { useUserProfileContext } from './UserProfile.context'
import type { CollectionListRef } from './WorkCollection'
import {
  AddToCollectionButton,
  CollectionList,
  CreateCollectionButton,
} from './WorkCollection'
import type { WorkTypes } from './WorkTypeTabs'
import WorkTypeTabs from './WorkTypeTabs'

const redirRootTab: Record<string, string> = {
  [ProfilePageTab.models]: 'artworks/models',
  [ProfilePageTab.manga]: 'artworks/manga',
  [ProfilePageTab.likedManga]: 'likes/liked-manga',
}

const TabVisibility = {
  SELF: 'SELF',
  ADMIN: 'ADMIN',
  SUPERADMIN: 'SUPERADMIN',
  AUTHENTICATED: 'AUTHENTICATED',
} as const

type TabVisibility = (typeof TabVisibility)[keyof typeof TabVisibility]

type TabEntry = [
  // Tab ID
  string,
  // Tab label
  ReactNode,
  // Visibility guards (OR semantics)
  TabVisibility[]?,
]

const useUserProfileTabs = (): TabEntry[] => {
  return compact([
    [
      ProfilePageTab.artworks,
      <Text key={ProfilePageTab.artworks} i18nKey="user:works.works.label" />,
    ],
    [
      ProfilePageTab.likes,
      <Text key={ProfilePageTab.likes} i18nKey="user:likes" />,
      [TabVisibility.AUTHENTICATED],
    ],
    [
      ProfilePageTab.tasks,
      <Text key={ProfilePageTab.tasks} i18nKey="user:generation-tasks" />,
      [TabVisibility.SELF, TabVisibility.SUPERADMIN],
    ],
    [
      ProfilePageTab.files,
      <Text
        key={ProfilePageTab.files}
        i18nKey="common:file.label"
        values={{ count: 2 }}
      />,
      [TabVisibility.ADMIN],
    ],
    [
      ProfilePageTab.credits,
      <Text
        key={ProfilePageTab.credits}
        i18nKey="user:membership-and-credits"
      />,
      [TabVisibility.SELF, TabVisibility.ADMIN],
    ],
    [
      ProfilePageTab.assets,
      <Text key={ProfilePageTab.assets} i18nKey="user:assets" />,
      [TabVisibility.SELF, TabVisibility.ADMIN],
    ],
    [
      ProfilePageTab.decorations,
      <Text
        key={ProfilePageTab.decorations}
        i18nKey="common:user-decoration.label"
        values={{ count: 2 }}
      />,
      [TabVisibility.AUTHENTICATED],
    ],
  ])
}

export default function UserProfileContent() {
  const { user, isMyProfile, tab: currentTab } = useUserProfileContext()
  const userProfileTabs = useUserProfileTabs()

  const { collections } = useCollections({
    userId: user.id,
  })

  const { t } = useTranslation(['common', 'user'])
  const { data: constants } = useDynamicConstants()
  const isCouponSystemEnabled = !!constants?.couponSystem?.enabled
  const defaultAssetTab = isCouponSystemEnabled ? 'coupons' : 'benefit-cards'
  const params = useParams()
  const { subTab: currentSubTab = '', collectionId } = params
  const currentTabInfo = useMemo(
    () => userProfileTabs.find(([id]) => id === currentTab),
    [currentTab, userProfileTabs],
  )
  const { data: collection } = useGetBookmark(collectionId ?? skipToken)
  const collectionListRef = useRef<CollectionListRef>(null)

  const { data: me } = useMyInfo()
  const platformRoles = me?.platformRoles ?? []
  const isAdmin = me?.isAdmin // migrate isAdmin to platformRoles in furture PRs
  const navigate = useNavigate()

  /**/

  const trackPageView = useTrackPageView()
  const trackPageViewRef = useRef(trackPageView)
  trackPageViewRef.current = trackPageView

  useEffect(() => {
    match(currentTab)
      .with(ProfilePageTab.artworks, () => {
        trackPageViewRef.current({
          scene: isMyProfile ? 'profile-works' : 'other-works',
          section: match(currentSubTab)
            .with('animations', () => 'animations')
            .with('models', () => 'models')
            .when(
              value => !value,
              () => 'artworks',
            )
            .otherwise(() => undefined),
        })
      })
      .with(ProfilePageTab.likes, () => {
        trackPageViewRef.current({
          scene: isMyProfile ? 'likes' : 'other-likes',
          section: match(currentSubTab)
            .with(ProfilePageTab.likedModel, () => 'model')
            .with(ProfilePageTab.likedLora, () => 'lora')
            .when(
              value => !value,
              () => 'artwork',
            )
            .otherwise(() => undefined),
        })
      })
      .otherwise(noop)
  }, [user, isMyProfile, currentTab, currentSubTab])

  const invalidateCouponList = useInvalidateCouponList()
  const prevTabRef = useRef({ tab: currentTab, subTab: currentSubTab })

  useEffect(() => {
    const prevTab = prevTabRef.current
    const wasCouponTab =
      prevTab.tab === ProfilePageTab.assets && prevTab.subTab === 'coupons'
    const isCouponTab =
      currentTab === ProfilePageTab.assets && currentSubTab === 'coupons'

    if (wasCouponTab && !isCouponTab) {
      invalidateCouponList()
    }

    prevTabRef.current = { tab: currentTab, subTab: currentSubTab }
  }, [currentTab, currentSubTab, invalidateCouponList])

  /**/

  const redirTo = redirRootTab[currentTab]
  if (redirTo) return <Navigate to={`/@${user.username}/${redirTo}`} replace />

  if (currentTab === ProfilePageTab.assets && !currentSubTab) {
    return (
      <Navigate
        to={`/@${user.username}/${ProfilePageTab.assets}/${defaultAssetTab}`}
        replace
      />
    )
  }

  const canSeeTab = (visibilities?: TabVisibility[]) => {
    if (!visibilities || visibilities.length === 0) return true
    return visibilities.some(visibility => {
      switch (visibility) {
        case TabVisibility.SELF:
          return isMyProfile
        case TabVisibility.ADMIN:
          return isAdmin
        case TabVisibility.SUPERADMIN:
          return platformRoles.includes(PLATFORM_ROLES.SUPER_ADMIN)
        case TabVisibility.AUTHENTICATED:
          return !!me
      }
    })
  }

  // When the visitor has no access to the tab, redirect to the default tab.
  if (!import.meta.env.DEV && !canSeeTab(currentTabInfo?.[2])) {
    return <Navigate to={`/@${user.username}`} replace />
  }

  const allowedToViewLikes = isMyProfile || isAdmin

  return (
    <>
      <MetaTags
        title={getUserTitle(t, user)}
        description={getUserMetaDescription(t, user, currentTab)}
        ogUrlPathname={`/@${user.username}/artworks`}
      />
      <div className="w-full">
        {/*
          Don't gate this section on `loadingCollections`: SSR / first
          hydrate render reports `loading: true` because the
          `useListUserBookmarks` query is a cache miss, which would
          flash an empty <CollectionList> shell on visitor profiles
          (no collections, not own profile) and then collapse it once
          the query resolves with `[]`. Keying purely on the resolved
          state (`collections.length > 0 || isMyProfile`) means the
          section either never appears (visitor with no collections —
          common case) or pops in once on first paint when the user
          actually owns the profile / actually has collections.
        */}
        {(collections?.length > 0 || isMyProfile) && (
          <section className="pb-8">
            <div className="mb-4 flex items-center">
              <h2 className="font-bold">
                {t('user:collection.section.title')}
              </h2>
              <div className="contents first:*:ml-auto">
                {isMyProfile && (
                  <CreateCollectionButton
                    i18nNamespace="collection"
                    onSubmit={bookmark => {
                      collectionListRef.current?.reload(true)
                      navigate(`/@${user.username}/collection/${bookmark.id}`)
                    }}
                  />
                )}
              </div>
            </div>
            <CollectionList
              ref={collectionListRef}
              userId={user.id}
              username={user.username}
              currentBookmarkId={collectionId}
            />
          </section>
        )}

        <Tabs
          value={currentTab}
          className="border-b"
          variant="scrollable"
          allowScrollButtonsMobile
          scrollButtons="auto"
        >
          {collectionId
            ? [
                <Tab
                  key="back"
                  component={Link}
                  to={`/@${user.username}/${ProfilePageTab.artworks}`}
                  value="none"
                  label={
                    <span className="flex gap-2 items-center normal-case">
                      <FluentChevronLeft20Regular className="size-5" />
                      {t('user:works.works.label', { context: 'back' })}
                    </span>
                  }
                  className="px-4 py-2 font-medium text-lg relative no-underline hover:bg-tab-hover active:bg-tab-hover rounded-t-lg whitespace-nowrap"
                />,
                <Tab
                  key="collection"
                  value={currentTab}
                  label={
                    <span className="normal-case">
                      {isDefaultCollection(collection?.name)
                        ? t('user:collection.default.name')
                        : collection?.name}
                    </span>
                  }
                  className="px-4 py-2 font-medium text-lg relative no-underline hover:bg-tab-hover active:bg-tab-hover rounded-t-lg whitespace-nowrap"
                />,
              ]
            : userProfileTabs.map(([tab, label, visibilities]) => {
                if (!import.meta.env.DEV && !canSeeTab(visibilities)) {
                  return null
                }
                const tabPath =
                  tab === ProfilePageTab.assets
                    ? `/@${user.username}/${ProfilePageTab.assets}/${defaultAssetTab}`
                    : `/@${user.username}/${tab}`
                return (
                  <Tab
                    key={tab}
                    component={Link}
                    to={tabPath}
                    value={tab}
                    label={<span className="normal-case">{label}</span>}
                    className="px-4 py-2 font-medium text-lg relative no-underline hover:bg-tab-hover active:bg-tab-hover rounded-t-lg whitespace-nowrap"
                    onClick={() => {
                      saveProfileScroll()
                      track('user_profile_tab_click', {
                        tab,
                      })
                    }}
                  />
                )
              })}
        </Tabs>

        <div className="mt-4 min-h-[50vh]">
          {/*
            Previously wrapped in `<Suspense fallback={<Preloader />}>`
            as a safety net for suspending hooks below. With
            `useSuspense: false` in our i18next config and no
            `useSuspenseQuery` / `React.lazy` / `dynamic({ ssr:
            false })` in the artworks-tab path, the boundary catches
            nothing useful — but React 19's streaming Suspense
            protocol still emits the placeholder + hidden swap
            target + swap script the moment **anything** in the
            subtree throws server-side, leaving no-JS / NoScript
            viewers of `/zh/@<user>/artworks` looking at a blank
            grid (real content hidden in `<div id="S:N">`, swap
            script never runs). Removing the boundary lets the
            artworks grid HTML inline into `<main>` so SSR is
            self-rendering again.
          */}
          {match(currentTab)
            .with(ProfilePageTab.artworks, () => (
              <WorkList collectionListRef={collectionListRef} />
            ))
            .with(ProfilePageTab.likes, () => {
              return (
                <UserProfileSubTab
                  currentTab={currentSubTab}
                  tabs={[
                    [
                      '',
                      t('user:profile-sub-tab.artwork'),
                      <UserLikedArtworkList key="artworks" userId={user.id} />,
                    ],
                    ...(allowedToViewLikes
                      ? ([
                          [
                            ProfilePageTab.likedModel,
                            t('user:model-sub-tab.sdv1model') as string,
                            <UserLikedModelItemList
                              key="models"
                              modelType={GenerationModelType.AnyModel}
                              userId={user.id}
                            />,
                          ],
                          [
                            ProfilePageTab.likedLora,
                            t('user:model-sub-tab.lora') as string,
                            <UserLikedModelItemList
                              key="loras"
                              modelType={GenerationModelType.AnyLora}
                              userId={user.id}
                            />,
                          ],
                        ] as [string, ReactNode, ReactNode][])
                      : []),
                  ]}
                  onChange={tab =>
                    navigateWithScrollLock(
                      navigate,
                      `/@${user.username}/${ProfilePageTab.likes}/${tab}`,
                    )
                  }
                />
              )
            })
            .with(ProfilePageTab.tasks, () => (
              <ProfileTaskTab
                currentTab={currentSubTab}
                onChange={tab =>
                  navigateWithScrollLock(
                    navigate,
                    `/@${user.username}/${ProfilePageTab.tasks}/${tab}`,
                  )
                }
                tabs={[
                  {
                    value: '',
                    label: t('user:profile-sub-tab.all'),
                    children: <ProfileTaskItemList userId={user.id} />,
                  },
                  ...(allowedToViewLikes
                    ? [
                        {
                          value: ProfilePageTab.bookmarks,
                          label: (
                            <span
                              className="inline-flex items-center gap-1"
                              key="favorites"
                            >
                              <IcBaselineStar className="size-4" />
                              {t('user:profile-sub-tab.favorites.label')}
                            </span>
                          ),
                          children: <ProfileBookmarkItemList />,
                        },
                      ]
                    : []),
                ]}
              />
            ))
            .with(ProfilePageTab.files, () => <ProfileFilesTab />)
            .with(ProfilePageTab.credits, () => (
              <UserCreditView key="credits" userId={user.id} />
            ))
            .with(ProfilePageTab.assets, () => (
              <UserAssetsTab
                userId={user.id}
                currentTab={currentSubTab}
                isCouponSystemEnabled={isCouponSystemEnabled}
                onChange={tab =>
                  navigateWithScrollLock(
                    navigate,
                    `/@${user.username}/${ProfilePageTab.assets}/${tab}`,
                  )
                }
              />
            ))
            .with(ProfilePageTab.decorations, () => (
              <UserDecorationsTab
                currentTab={currentSubTab}
                onChange={tab =>
                  navigateWithScrollLock(
                    navigate,
                    `/@${user.username}/${ProfilePageTab.decorations}/${tab}`,
                  )
                }
              />
            ))
            .otherwise(() => null)}
        </div>
      </div>
    </>
  )
}

function WorkList(props: {
  collectionListRef: RefObject<CollectionListRef | null>
}) {
  const { subTab: currentSubTab = '', collectionId } = useParams()
  const workType = mapPathToWorkType(currentSubTab ?? '')

  // oxfmt-ignore
  const [collectionItemType, setCollectionItemType] = useState<WorkTypes | ''>('')

  if (!collectionId && collectionItemType !== '') {
    setCollectionItemType('')
  }

  const { user, isMyProfile } = useUserProfileContext()
  const navigate = useNavigate()
  const { data: me } = useMyInfo()
  const isAdmin = me?.isAdmin

  const trackPageView = useTrackPageView()
  const trackPageViewRef = useRef(trackPageView)
  trackPageViewRef.current = trackPageView

  useEffect(() => {
    if (collectionId) {
      trackPageViewRef.current({
        scene: 'collection',
        section: collectionItemType || 'all',
      })
    }
  }, [collectionId, collectionItemType])

  return (
    <>
      <WorkTypeTabs
        value={collectionId ? collectionItemType : workType}
        accept={collectionId ? ['artwork', 'model'] : undefined} // @deprecated at 2025-07-23: remove 'manga' type
        onChange={value => {
          if (collectionId) {
            setCollectionItemType(value === collectionItemType ? '' : value)
          } else if (value) {
            track('my_work_tab_click', { tab: value })
            // `/@username/artworks/` is for artwork list
            // oxfmt-ignore
            navigateWithScrollLock(navigate, `/@${user.username}/${ProfilePageTab.artworks}/${mapWorkTypeToPath(value)}`)
          }
        }}
        className="pb-4"
      />
      {match(workType)
        .with('artwork', 'animation', () => (
          <PersonalArtworkList
            userId={user.id}
            username={user.username}
            isMe={isMyProfile}
            isAdmin={isAdmin}
            type={collectionId ? collectionItemType : workType}
            collectionListRef={props.collectionListRef}
          />
        ))
        .with('model', () => <UserModelTabContent isAdmin={isAdmin} />)
        // @deprecated at 2025-07-23
        // .with('manga', () => (
        //   <PersonalMangaList userId={user.id} isAdmin={isAdmin} />
        // ))
        .exhaustive()}
    </>
  )
}

function mapPathToWorkType(path: string): WorkTypes {
  switch (path) {
    case '':
      return 'artwork'
    // @deprecated at 2025-07-23
    // case 'manga':
    //   return 'manga'
    case 'animations':
      return 'animation'
    case 'models':
      return 'model'
    default:
      shouldNotHappen()
  }
}

function mapWorkTypeToPath(workType: WorkTypes) {
  switch (workType) {
    case 'artwork':
      return ''
    // @deprecated at 2025-07-23
    // case 'manga':
    //   return 'manga'
    case 'animation':
      return 'animations'
    case 'model':
      return 'models'
  }
}

const MODEL_TAB_QS_KEYS = ['sort', 'after', 'before'] as const

function UserModelTabContent(props: { isAdmin?: boolean }) {
  const { t } = useTranslation(['common', 'action', 'user'])
  const { user, isMyProfile } = useUserProfileContext()

  const orderOptions = [
    [OrderOption.newest, t('common:order-option.newest')],
    [OrderOption.oldest, t('common:order-option.oldest')],
    [OrderOption.mostLiked, t('common:order-option.most-liked')],
    [OrderOption.mostUsed, t('common:order-option.most-used')],
  ]

  const [{ sort: querySort, after: queryAfter, before: queryBefore }] = useQs()
  const [hash, setHash] = useHash()
  const hashParams = useMemo(
    () => new URLSearchParams(hash.replace(/^#/, '')),
    [hash],
  )

  const queryValues = useMemo(
    () => ({
      sort: querySort,
      after: queryAfter,
      before: queryBefore,
    }),
    [querySort, queryAfter, queryBefore],
  )

  // Migrate from query params to hash params (one-time migration)
  useEffect(() => {
    const hasQueryParams = MODEL_TAB_QS_KEYS.some(key => queryValues[key])
    if (!hasQueryParams) return

    const url = new URL(window.location.href)
    for (const key of MODEL_TAB_QS_KEYS) {
      url.searchParams.delete(key)
    }

    const params = new URLSearchParams(url.hash.replace(/^#/, ''))
    for (const key of MODEL_TAB_QS_KEYS) {
      const value = queryValues[key]
      if (value) {
        params.set(key, value)
      } else {
        params.delete(key)
      }
    }
    url.hash = params.toString()
    window.history.replaceState(null, '', url.toString())

    setHash(params.toString())
  }, [queryValues, setHash])

  const sort = hashParams.get('sort') || undefined
  const after = hashParams.get('after') || undefined
  const before = hashParams.get('before') || undefined

  const onFilterChange = (args: Record<string, string | null>) => {
    const params = new URLSearchParams(hash.replace(/^#/, ''))
    // Clear pagination params
    params.delete('before')
    params.delete('after')
    // Set filters in hash
    if (args.sort) {
      params.set('sort', args.sort)
    } else {
      params.delete('sort')
    }
    setHash(params.toString())
  }

  const onCursorChange = ({
    after: newAfter,
    before: newBefore,
  }: {
    after?: string
    before?: string
  }) => {
    const params = new URLSearchParams(hash.replace(/^#/, ''))
    if (newAfter) {
      params.set('after', newAfter)
      params.delete('before')
    } else if (newBefore) {
      params.set('before', newBefore)
      params.delete('after')
    } else {
      params.delete('after')
      params.delete('before')
    }
    setHash(params.toString())
  }

  const editState = useEditItems({
    actionButtonName: t('action:manage'),
  })
  const [updatingCollection, addCollectionItems] = useLoadingCallback(
    async (collection: Bookmark) => {
      if (editState.selectedItems.length === 0) return
      try {
        await Promise.all(
          editState.selectedItems.map(modelId =>
            addCollectionItem(collection.id, { modelId }),
          ),
        )
        editState.cancelSelected()
        toast.success(t('user:collection.notice.add.success'))
      } catch {
        toast.error(t('user:collection.notice.add.failed'))
      }
    },
    [editState],
  )

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <FormControl className="w-[200px]" size="small">
          <InputLabel>{t('common:order-option.sort')}</InputLabel>
          <Select
            label={t('common:order-option.sort')}
            value={sort || orderOptions[0][0]}
            onChange={e => {
              onFilterChange({
                sort: e.target.value,
              })
            }}
          >
            {orderOptions.map(([value, label]) => {
              return (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
        <span className="ml-auto" />
        {isMyProfile && !editState.editing && <ModelListMoreActions />}
        {/* Don't show manage button unless is self profile or admin */}
        {(isMyProfile || props.isAdmin) && editState.editItemsAction}
        {editState.editing && (
          <AddToCollectionButton
            userId={user.id}
            loading={updatingCollection}
            selectedItems={editState.selectedItems}
            addItemsToCollection={addCollectionItems}
          />
        )}
      </div>
      <UserModelList
        userId={user.id}
        filters={{
          sort: sort ?? '',
        }}
        editing={editState.editing}
        selectedItems={editState.selectedItems}
        onClickModel={model => editState.toggleSelected(model.id)}
        cursorState={{ before, after }}
        onCursorChange={onCursorChange}
      />
    </>
  )
}

function ModelListMoreActions() {
  return (
    <div className="flex items-center gap-2">
      <NavToTrainLoraPageButton from="user-profile" />
      <UILink
        variant="outlined"
        href="/market/submit"
        className="rounded-lg font-medium h-[36.5px]"
        onPress={() => {
          track('upload_lora_model_click', {
            location: 'user-profile',
          })
        }}
      >
        <Text i18nKey="artwork:upload-local-image" />
      </UILink>
    </div>
  )
}
